import { DialogTeamCreation } from "@/components/admin/dialog-team-creation";
import AvatarCompany from "@/components/company/avatar-company";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAdminMode } from "@/contexts/admin-mode-provider";
import { useTeams } from "@/contexts/teams-provider";
import { useUser } from "@/contexts/user-provider";
import { TeamUserRolesEnum } from "@/enums/team-user-role";
import { useTranslation } from "@/hooks/useTranslation";
import { groupSwitchableTeams, isTeamListed } from "@/lib/team-switcher";
import { cn } from "@/lib/utils";
import {
  CaretSortIcon,
  CheckIcon,
  PlusCircledIcon,
} from "@radix-ui/react-icons";
import { Toggle } from "@radix-ui/react-toggle";
import { AdminTeamDTO, CompanyDTO, TeamDTO } from "@shaarp/common";
import { TriangleAlertIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { RiToggleFill, RiToggleLine } from "react-icons/ri";
import { DialogAdminTeamCreation } from "../admin/dialog-admin-team-creation";
import { DialogAdminUserInvitation } from "../admin/dialog-admin-user-invitation";
import { Badge } from "../ui/badge";

type PopoverTriggerProps = React.ComponentPropsWithoutRef<
  typeof PopoverTrigger
>;

interface TeamSwitcherProps extends PopoverTriggerProps {}

export default function NavTeamSwitcher({ className }: TeamSwitcherProps) {
  const { teams, teamSelected, teamCompanySelected, setTeamIdSelected } =
    useTeams();
  const { isAdminMode } = useAdminMode();
  const { t } = useTranslation();
  const { user } = useUser();

  const {
    own: userTeams,
    affiliated: userAffiliatedTeams,
    supported: userTeamsAdmin,
  } = useMemo(() => groupSwitchableTeams(teams ?? []), [teams]);

  const [open, setOpen] = useState(false);
  const [showTeamsDisabled, setShowTeamsDisabled] = useState(false);
  const [showAdminNewTeamDialog, setShowAdminNewTeamDialog] = useState(false);
  const [showNewTeamDialog, setShowNewTeamDialog] = useState(false);
  const [showNewUserDialog, setShowNewUserDialog] = useState(false);


  const selectTeam = (team: TeamDTO) => {
    setTeamIdSelected(team.id);
    window.location.reload();
  };

  const onNewTeamCreated = (newTeam: AdminTeamDTO) => {
    setTeamIdSelected(newTeam.id);
    window.location.reload();
  };

  const filterTeams = (value: string, search: string) => {
    return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
  };

  if (!teams.length) return null;

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label={t("teamSwitcher.selectTeam")}
            className={cn("justify-between w-[200px]", className)}
          >
            <AvatarCompany
              company={
                teamCompanySelected ?? ({ name: teamSelected?.name } as CompanyDTO)
              }
              className="mr-1.5 w-5 h-5 shrink-0 text-[8px]"
            />
            <span className="truncate">{teamSelected?.name}</span>
            <CaretSortIcon className="ml-auto w-4 h-4 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0">
          <Command filter={filterTeams}>
            {teams.length > 5 && (
              <>
                <div className="relative">
                  <CommandInput
                    placeholder={t("teamSwitcher.search")}
                    className="flex-1"
                    onValueChange={(value) => setShowTeamsDisabled(!!value)}
                  />
                  {isAdminMode && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Toggle
                          pressed={showTeamsDisabled}
                          onPressedChange={setShowTeamsDisabled}
                          asChild
                        >
                          <Button
                            size="default"
                            variant="ghost"
                            className="absolute top-0 right-0 h-full"
                          >
                            {showTeamsDisabled ? (
                              <RiToggleFill className="size-4" />
                            ) : (
                              <RiToggleLine className="size-4" />
                            )}
                          </Button>
                        </Toggle>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          {showTeamsDisabled
                            ? t("teamSwitcher.hideDisabled")
                            : t("teamSwitcher.showDisabled")}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <CommandEmpty>{t("teamSwitcher.noTeamsFound")}</CommandEmpty>
              </>
            )}
            <CommandList>
              <CommandGroup key="teams" heading={t("teamSwitcher.teams")}>
                {userTeams
                  .filter((team) => isTeamListed(team, showTeamsDisabled))
                  .map((team) => (
                    <CommandItemTeam
                      key={team.id}
                      team={team}
                      teamSelected={teamSelected}
                      onSelect={() => {
                        selectTeam(team);
                        setOpen(false);
                      }}
                    />
                  ))}
              </CommandGroup>
            </CommandList>
            {user?.isAdvisor && userAffiliatedTeams.length > 0 && (
              <>
                <CommandSeparator />
                <CommandList>
                  <CommandGroup heading={t("teamSwitcher.affiliatedTeams")}>
                    {userAffiliatedTeams.map((team) => (
                      <CommandItemTeam
                        key={team.id}
                        team={team}
                        teamSelected={teamSelected}
                        onSelect={() => {
                          selectTeam(team);
                          setOpen(false);
                        }}
                      />
                    ))}
                  </CommandGroup>
                </CommandList>
              </>
            )}
            {isAdminMode && (
              <>
                <CommandSeparator />
                <CommandList>
                  <CommandGroup
                    key="teamsAdmin"
                    heading={t("teamSwitcher.otherTeamsSupport")}
                    className="bg-warning/20"
                  >
                    {userTeamsAdmin
                      .filter((team) => isTeamListed(team, showTeamsDisabled))
                      .map((team) => (
                        <CommandItemTeam
                          key={team.id}
                          team={team}
                          teamSelected={teamSelected}
                          hideRole
                          onSelect={() => {
                            selectTeam(team);
                            setOpen(false);
                          }}
                        />
                      ))}
                  </CommandGroup>
                </CommandList>
                <CommandSeparator />
                <CommandList>
                  <CommandGroup className="bg-warning/20">
                    <CommandItem
                      onSelect={() => {
                        setOpen(false);
                        setShowAdminNewTeamDialog(true);
                      }}
                    >
                      <PlusCircledIcon className="mr-2 w-5 h-5" />
                      {t("teamSwitcher.newTeamAdmin")}
                    </CommandItem>
                    <CommandItem
                      onSelect={() => {
                        setOpen(false);
                        setShowNewUserDialog(true);
                      }}
                    >
                      <PlusCircledIcon className="mr-2 w-5 h-5" />
                      {t("teamSwitcher.newUser")}
                    </CommandItem>
                  </CommandGroup>
                </CommandList>
              </>
            )}
            {user?.isAdvisor && (
              <>
                <CommandSeparator />
                <CommandList>
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => {
                        setOpen(false);
                        setShowNewTeamDialog(true);
                      }}
                    >
                      <PlusCircledIcon className="mr-2 w-5 h-5" />
                      {t("teamSwitcher.newTeam")}
                    </CommandItem>
                  </CommandGroup>
                </CommandList>
              </>
            )}
          </Command>
        </PopoverContent>
      </Popover>

      <DialogAdminTeamCreation
        open={showAdminNewTeamDialog}
        onCreated={onNewTeamCreated}
        onClose={() => setShowAdminNewTeamDialog(false)}
      />

      <DialogAdminUserInvitation
        open={showNewUserDialog}
        onClose={() => setShowNewUserDialog(false)}
      />

      <DialogTeamCreation
        open={showNewTeamDialog}
        onClose={() => setShowNewTeamDialog(false)}
      />
    </>
  );
}

const CommandItemTeam = ({
  team,
  teamSelected,
  onSelect = () => {},
  hideRole = false,
}: {
  team: TeamDTO;
  teamSelected: TeamDTO | null;
  onSelect?: () => void;
  hideRole?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <CommandItem
      key={team.id}
      value={`${team.id}-${team.name}`}
      onSelect={onSelect}
      className={`text-sm ${team.status === "disabled" && "bg-orange-100"}`}
    >
      <AvatarCompany
        company={
          {
            name: team.name,
            logo: team.companyLogo || null,
            domain: team.companyDomain || null,
          } as CompanyDTO
        }
        className="mr-1.5 w-5 h-5 shrink-0 text-[8px]"
      />
      {team.name}
      {team.status === "disabled" && (
        <Tooltip>
          <TooltipTrigger asChild>
            <TriangleAlertIcon className="ml-1 w-3 h-3" />
          </TooltipTrigger>
          <TooltipContent>
            <p>{t("teamSwitcher.teamDisabled")}</p>
          </TooltipContent>
        </Tooltip>
      )}
      {!hideRole && (
        <Badge variant="outline" className="ml-auto">
          {team.userRole === TeamUserRolesEnum.ADMIN &&
            t("teamSwitcher.roles.admin")}
          {team.userRole === TeamUserRolesEnum.MANAGER &&
            t("teamSwitcher.roles.manager")}
          {team.userRole === TeamUserRolesEnum.MEMBER &&
            t("teamSwitcher.roles.member")}
          {team.userRole === TeamUserRolesEnum.BILLING &&
            t("teamSwitcher.roles.billing")}
          {team.userRole === TeamUserRolesEnum.ADVISOR &&
            t("teamSwitcher.roles.advisor")}
        </Badge>
      )}
      <CheckIcon
        className={cn(
          "ml-2 h-4 w-4",
          teamSelected?.id === team.id ? "opacity-100" : "opacity-0",
        )}
      />
    </CommandItem>
  );
};
